import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from './cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: ReactNode
  rightSlot?: ReactNode
  invalid?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { leftIcon, rightSlot, invalid, className, ...rest },
  ref,
) {
  return (
    <div className="relative w-full">
      {leftIcon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle">
          {leftIcon}
        </span>
      )}
      <input
        ref={ref}
        className={cn(
          'w-full h-11 rounded-xl bg-surface text-ink placeholder:text-ink-subtle',
          'border border-line focus:border-brand focus:outline-none',
          'focus:ring-2 focus:ring-brand/30 transition-all duration-150',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          leftIcon ? 'pl-10' : 'pl-4',
          rightSlot ? 'pr-10' : 'pr-4',
          invalid && 'border-error focus:border-error focus:ring-error/30',
          className,
        )}
        {...rest}
      />
      {rightSlot && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle">
          {rightSlot}
        </span>
      )}
    </div>
  )
})

export interface FieldProps {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  children: ReactNode
  className?: string
}

export function Field({ label, hint, error, required, children, className }: FieldProps) {
  return (
    <label className={cn('block', className)}>
      {label && (
        <span className="mb-1.5 block text-sm font-medium text-ink">
          {label}
          {required && <span className="ml-0.5 text-error">*</span>}
        </span>
      )}
      {children}
      {(error || hint) && (
        <span className={cn('mt-1.5 block text-xs', error ? 'text-error' : 'text-ink-subtle')}>
          {error || hint}
        </span>
      )}
    </label>
  )
}
